import {JSX, useCallback, useEffect, useMemo, useReducer, useState} from 'react';
import {arrayMove} from '@dnd-kit/sortable';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import {Grid} from '@mui/system';
import { useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { isEmpty, isNil, toString, uniq } from 'lodash';
import { useSnackbar } from 'notistack';
import { RESET, SYSTEM_COLLAPSE, SYSTEM_EXPAND, USER_EXPAND } from 'common/actions/row-groups/types';
import { CAP_TABLE_CURRENCY_PAGE } from 'common/constants/currencyPageTypes';
import {
  FIRM_SUMMARY_DEFINITION_DATA_QUERY_KEY,
  FIRM_VALUATIONS_DEFINITION_DATA_QUERY_KEY,
} from 'common/constants/services/firms';
import { useFormat } from 'common/hooks';
import { rowsGroupsReducer } from 'common/reducers/rowsGroups';
import { RowsGroups } from 'common/reducers/rowsGroups/types';
import { useStore } from 'common/store';
import { UseFormatValues } from 'common/types/format';
import { ScalarSpreadsheetProps } from 'common/types/scalarSpreadsheet';
import { UseStoreValues } from 'common/types/store';
import { Filter, SortKey, SummaryGroupBy } from 'common/types/summaryDefinitions';
import { CustomCompactContainer, CustomContainer, CustomScalarSpreadsheet, Tab } from 'components';
import AIAssistantInput from 'components/AIAssistantInput';
import { REGULAR_UNIT } from 'components/FeaturedSpreadsheet/constants';
import {GridSkeleton} from 'components/Grid';
import useWorkbook from 'components/ScalarSpreadsheet/utilities/useWorkbook';
import WorkbookContext from 'components/ScalarSpreadsheet/utilities/WorkbookContext';
import ChartDataContext from 'components/WithChart/ChartDataContext';
import {useLayoutContext} from 'context/LayoutContext';
import {useSummaryDefinitions} from 'context/SummaryDefinitionsContext';
import { useExportSignal } from 'features/pdfExport';
import {
  FIRM_SUMMARY_CONTAINER_CLASSNAME,
  FIRM_SUMMARY_KEY,
  FIRM_SUMMARY_PROCESS_MANAGEMENT_COLUMN_TYPE,
  FIRM_SUMMARY_SPREADSHEET_ALL_FUNDS,
  FIRM_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
  FIRM_SUMMARY_SPREADSHEET_DEFAULT_CURRENCY,
  FIRM_SUMMARY_SPREADSHEET_NON_PARENT_ROW_GROUP,
  FIRM_SUMMARY_TITLE,
  FIRM_SUMMARY_VALUES_SHOWN_PUBLISHED_TITLE,
} from 'pages/Firms/common/constants';
import {useFirmMeasurementDateFilter} from 'pages/Firms/hooks/useFirmMeasurementDateFilter';
import {FirmSummarySpreadsheet, useFirmSummarySheet} from 'pages/Firms/hooks/useFirmSummarySheet';
import {
  getOrderedColumnTypes,
  getRenderedFirmSummaryColumnTypes,
} from 'pages/Firms/hooks/useFirmSummarySheet/config/createColumns';
import FilterChipsBar from 'pages/Firms/pages/FirmSummary/FilterChipsBar';
import SortChipsBar from 'pages/Firms/pages/FirmSummary/SortChipsBar';
import { InformationMessage, TaskProgressDialog } from 'pages/Funds/components';
import type { ProcessManagementTaskDialogData } from 'pages/Funds/components/ProcessManagement';
import { useIntelligenceContext } from 'pages/Intelligence/IntelligenceContext';
import { PdfExportTargets } from 'pages/Valuations/util/constants';
import {
  useGetFirmChartData,
  useGetFirmSummaryData,
  useGetFirmValuationsChartData,
  useGetFirmValuationsData,
  useGetTasksProgressByFirm,
} from 'services/hooks/firms';
import { SummaryRow } from 'services/hooks/firms/useGetFirmSummaryData';
import { useAIAssistSummary, useAIAssistValuationsSummary } from 'services/hooks/summaryDefinitions';
import { generateColumnKey, getArrayValue, getObjectValue, isValidDate } from 'utilities';
import { dbShortDate, ISO_DATE_FORMAT } from 'utilities/dateFormats';
import { FundByCompany, UseFirmSummaryTabParams } from './useFirmSummaryTab.types';

const toNullableString = (value: unknown): string | null =>
  value === null || value === undefined ? null : toString(value);

const getSummaryRowMeasurementDate = (row: SummaryRow, selectedMeasurementDate?: string) =>
  toNullableString(row.measurement_date ?? selectedMeasurementDate) ?? '';

const getSummaryRowCapTableCurrency = (row: SummaryRow): string =>
  row.cap_table_currency ?? row.financials_currency ?? FIRM_SUMMARY_SPREADSHEET_DEFAULT_CURRENCY;

type SummaryRowWithCurrency = SummaryRow & {
  exchange_rate_to_fund_currency?: number | null;
  fund_currency?: string | null;
};

const getSummaryRowExchangeRate = (row: SummaryRowWithCurrency): string =>
  toNullableString(row.exchange_rate_to_fund_currency) ?? '1';

const normalizeDefinitionRows = (rows: SummaryRow[], measurementDate?: string): FundByCompany[] => {
  const parentKeyByCompany = new Map<string, string>();
  rows.forEach(row => {
    if (row.row_type === 'company_rollup') {
      const key = generateColumnKey({
        id: row.company_name ?? '',
        name: row.fund_name ?? '',
        prefix: FIRM_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
      });
      parentKeyByCompany.set(row.company_name ?? '', key);
    }
  });

  return rows.map(row => {
    const summaryRow = row as SummaryRowWithCurrency;
    const isRollup = row.row_type === 'company_rollup';
    const isCompany = row.row_type === 'company';
    const parentKey = isCompany ? (parentKeyByCompany.get(row.company_name ?? '') ?? null) : null;
    const rowMeasurementDate = getSummaryRowMeasurementDate(row, measurementDate);
    const capTableCurrency = getSummaryRowCapTableCurrency(row);

    return {
      ...FIRM_SUMMARY_SPREADSHEET_NON_PARENT_ROW_GROUP,
      company_id: row.id,
      company_name: row.company_name,
      company_slug: row.company_slug ?? '',
      company_captable_currency: capTableCurrency,
      fund_id: 0,
      fund_name: row.fund_name,
      fund_slug: row.fund_slug ?? '',
      fund_currency: summaryRow.fund_currency ?? FIRM_SUMMARY_SPREADSHEET_DEFAULT_CURRENCY,
      exchange_rate_to_fund_currency: getSummaryRowExchangeRate(summaryRow),
      measurement_date: rowMeasurementDate,
      initial_investment_date: row.initial_investment_date,
      most_recent_investment_date: row.most_recent_investment_date,
      diluted_ownership: toNullableString(row.diluted_ownership),
      outstanding_ownership: toNullableString(row.outstanding_ownership),
      fund_cost_basis: toNullableString(row.invested_capital),
      realized_value: toNullableString(row.realized_value),
      unrealized_value: toNullableString(row.unrealized_value),
      total_value: toNullableString(row.total_value),
      gross_irr: toNullableString(row.gross_irr),
      moic: toNullableString(row.moic),
      x_factor: null,
      is_published: true,
      is_final: true,
      breakeven_exit_equity_value_min: toNullableString(row.breakeven_exit_equity_value_min),
      breakeven_exit_equity_value_max: toNullableString(row.breakeven_exit_equity_value_max),
      allocation_method: row.allocation_method ?? '',
      equity_value: toNullableString(row.equity_value),
      enterprise_valuation_method: row.enterprise_valuation_method ?? '',
      enterprise_value: toNullableString(row.enterprise_value),
      current_fund_value: toNullableString(row.current_fund_value ?? row.unrealized_value),
      percent_change_from_previous: toNullableString(row.percent_change_from_previous),
      valuation_status: row.valuation_status ?? '',
      total_insight_preference: toNullableString(row.total_insight_preference),
      fund_preference: toNullableString(row.fund_preference),
      specific_preference: toNullableString(row.specific_preference),
      total_company_preference: toNullableString(row.total_company_preference),
      implied_arr_multiple: toNullableString(row.implied_arr_multiple),
      valuation_approach: row.valuation_approach ?? '',
      dcf_discount_rate: toNullableString(row.dcf_discount_rate),
      projected_revenue_year_1: toNullableString(row.projected_revenue_year_1),
      projected_revenue_year_2: toNullableString(row.projected_revenue_year_2),
      projected_revenue_year_3: toNullableString(row.projected_revenue_year_3),
      funds_count: isRollup ? (row.funds_count ?? 1) : 1,
      is_parent: isRollup,
      parent_key: parentKey,
      parent_type: 'company',
    } as FundByCompany;
  });
};

const useFirmSummaryTab = (params: UseFirmSummaryTabParams) => {
  const {
    spreadsheetDefinition,
    definitionId,
    onSortToggle,
    valueTypeById,
    onClearFilters,
    summaryType,
    onRemoveFilter,
    onClearSorts,
    onRemoveSort,
    onAddColumn,
  } = params ?? {};;
  const [storeValue] = useStore() as UseStoreValues;
  const { companyList: currentCompanies, firmInfo: currentFirm } = getObjectValue(storeValue);
  const { id: currentFirmId } = getObjectValue(currentFirm);
  const [selectedProcessManagementTask, setSelectedProcessManagementTask] =
    useState<ProcessManagementTaskDialogData | null>(null);
  const closeProcessManagementTaskDialog = useCallback(() => {
    setSelectedProcessManagementTask(null);
  }, []);

  const isValuations = summaryType === 'valuations';
  const isProcessManagementEnabled =
    isValuations && Boolean(getObjectValue(currentFirm).show_process_management_column);
  const isEnhancedMode = !isNil(definitionId);

  const { activeColumnWidths, setColumnWidth, setColumnOrder, updateDefinition, activeDefinition, refetch } =
    useSummaryDefinitions();

  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const aiAssistFirm = useAIAssistSummary(currentFirmId);
  const aiAssistValuations = useAIAssistValuationsSummary(currentFirmId);
  const aiAssist = isValuations ? aiAssistValuations : aiAssistFirm;

  const handleAIAssistSubmit = useCallback(
    async (prompt: string) => {
      if (!spreadsheetDefinition || !activeDefinition) {
        return;
      }
      try {
        const result = await aiAssist.mutateAsync({
          prompt,
          current_definition: spreadsheetDefinition,
        });

        await updateDefinition(activeDefinition.id, {
          spreadsheet_definition: result.spreadsheet_definition,
        });

        const definitionDataQueryKey = isValuations
          ? FIRM_VALUATIONS_DEFINITION_DATA_QUERY_KEY
          : FIRM_SUMMARY_DEFINITION_DATA_QUERY_KEY;
        await Promise.all([
          refetch(),
          queryClient.invalidateQueries({ queryKey: [definitionDataQueryKey] }),
        ]);

        if (result.message) {
          enqueueSnackbar(result.message, { variant: 'success' });
        }
      } catch {
        enqueueSnackbar('AI assistant is temporarily unavailable. Please try again.', { variant: 'error' });
      }
    },
    [activeDefinition, aiAssist, enqueueSnackbar, isValuations, queryClient, refetch, spreadsheetDefinition, updateDefinition]
  );

  const orderedColumnTypes = useMemo(
    () => getOrderedColumnTypes(spreadsheetDefinition),
    [spreadsheetDefinition]
  );

  const renderedColumnTypes = useMemo(
    () => getRenderedFirmSummaryColumnTypes(orderedColumnTypes, isProcessManagementEnabled),
    [isProcessManagementEnabled, orderedColumnTypes]
  );

  const handleColumnReorder = useCallback(
    async (sourceColumn: string, targetColumn: string) => {
      if (
        sourceColumn === FIRM_SUMMARY_PROCESS_MANAGEMENT_COLUMN_TYPE ||
        targetColumn === FIRM_SUMMARY_PROCESS_MANAGEMENT_COLUMN_TYPE
      ) {
        return;
      }

      const sourceIdx = orderedColumnTypes.indexOf(sourceColumn);
      const targetIdx = orderedColumnTypes.indexOf(targetColumn);
      if (sourceIdx === -1 || targetIdx === -1 || sourceIdx === targetIdx) {
        return;
      }
      const newOrder = arrayMove(orderedColumnTypes, sourceIdx, targetIdx);

      // getOrderedColumnTypes filters out columns like company_name (handled separately by row headers).
      // Re-inject them at the front in their original sort order so the backend definition retains them.
      const orderedSet = new Set(orderedColumnTypes);
      const preservedColumns = Object.entries(spreadsheetDefinition?.columns ?? {})
        .sort(([a], [b]) => Number(a) - Number(b))
        .map(([, col]) => col)
        .filter(col => !orderedSet.has(col));

      const fullOrder = [...preservedColumns, ...newOrder];
      const columns = Object.fromEntries(fullOrder.map((col, idx) => [idx, col]));
      await setColumnOrder(columns);
    },
    [orderedColumnTypes, setColumnOrder, spreadsheetDefinition]
  );

  const handleColumnResize = useCallback(
    (column: string, width: number) => {
      if (column === FIRM_SUMMARY_PROCESS_MANAGEMENT_COLUMN_TYPE) {
        return;
      }

      setColumnWidth(column, width);
    },
    [setColumnWidth]
  );

  // Handle measurement date filter, query param, and context synchronization
  const {
    hasNoMeasurementDates,
    isLoadingMeasurementDate,
    isMostRecentMeasurementDate,
    mostRecentMeasurementDate,
    selectedMeasurementDate,
  } = useFirmMeasurementDateFilter();

  const resolvedMeasurementDate =
    !isNil(selectedMeasurementDate) && isValidDate(selectedMeasurementDate.date)
      ? selectedMeasurementDate.date
      : undefined;

  const resolvedMeasurementDateForEnhanced = isMostRecentMeasurementDate ? undefined : resolvedMeasurementDate;
  const processManagementMeasurementDate = isMostRecentMeasurementDate ? mostRecentMeasurementDate : selectedMeasurementDate;
  const processManagementMeasurementDateName = useMemo(
    () =>
      dbShortDate(
        processManagementMeasurementDate?.name ?? processManagementMeasurementDate?.date,
        ISO_DATE_FORMAT
      ) ?? '',
    [processManagementMeasurementDate?.date, processManagementMeasurementDate?.name]
  );

  const [format, formatDispatch] = useFormat({
    measurementDate: isMostRecentMeasurementDate ? mostRecentMeasurementDate : selectedMeasurementDate,
    page: CAP_TABLE_CURRENCY_PAGE,
    sourceCurrency: FIRM_SUMMARY_SPREADSHEET_DEFAULT_CURRENCY,
    units: REGULAR_UNIT,
  }) as UseFormatValues;

  const {
    data: tasksProgress,
    isFetching: isFetchingTasksProgress,
    isLoading: isLoadingTasksProgress,
  } = useGetTasksProgressByFirm({
    firm: currentFirm,
    shouldQueryAutomatically: isProcessManagementEnabled,
  });

  // Fund selection lives in the Intelligence secondary navigation (sticky via ?fund=).
  // The context owns the firm-funds list query so the secondary-nav dropdown and the
  // in-spreadsheet FundSelector share the same source of truth.
  const {
    fundOptions,
    isAllFundsSelected,
    selectedFund,
    selectedFundId,
    setSelectedFund: setSelectedFundFromContext,
  } = useIntelligenceContext();

  // The in-spreadsheet FundSelector wants display names; the context's setter takes a
  // fund id (or the All Funds sentinel). Adapt name → id so both selectors share state.
  const fundsAvailable = useMemo(
    () => [FIRM_SUMMARY_SPREADSHEET_ALL_FUNDS, ...fundOptions.map(fund => fund.name)],
    [fundOptions]
  );

  const fundIdByName = useMemo<Map<string, number>>(() => {
    const map = new Map<string, number>();
    fundOptions.forEach(fund => {
      if (fund?.name && typeof fund.id === 'number' && !map.has(fund.name)) {
        map.set(fund.name, fund.id);
      }
    });
    return map;
  }, [fundOptions]);

  const setSelectedFund = useCallback(
    (fundName: string) => {
      if (fundName === FIRM_SUMMARY_SPREADSHEET_ALL_FUNDS) {
        setSelectedFundFromContext(FIRM_SUMMARY_SPREADSHEET_ALL_FUNDS);
        return;
      }
      const fundId = fundIdByName.get(fundName);
      if (typeof fundId === 'number') {
        setSelectedFundFromContext(String(fundId));
      }
    },
    [fundIdByName, setSelectedFundFromContext]
  );

  const enhancedDataGate =
    isEnhancedMode &&
    !isNil(currentFirmId) &&
    !isNil(definitionId) &&
    !isLoadingMeasurementDate &&
    // Wait for the fund map before issuing fund-scoped requests, otherwise
    // we'd briefly send no fund_id and return firm-wide rows.
    (isAllFundsSelected || !isNil(selectedFundId));

  const {
    data: firmDefinitionData,
    isFetched: isFetchedFirmDefinitionData,
    isFetching: isFetchingFirmDefinitionData,
    isLoading: isLoadingFirmDefinitionData,
  } = useGetFirmSummaryData({
    firmId: currentFirmId,
    definitionId,
    measurementDate: resolvedMeasurementDateForEnhanced,
    fundId: isEnhancedMode ? selectedFundId : undefined,
    shouldQueryAutomatically:
      !isValuations &&
      !isNil(currentFirmId) &&
      !isNil(definitionId) &&
      !isLoadingMeasurementDate &&
      // Wait for the fund map before issuing fund-scoped requests, otherwise
      // we'd briefly send no fund_id and return firm-wide rows.
      (isAllFundsSelected || !isNil(selectedFundId)),
  });

  const {
    data: valuationsDefinitionData,
    isFetched: isFetchedValuationsDefinitionData,
    isFetching: isFetchingValuationsDefinitionData,
    isLoading: isLoadingValuationsDefinitionData,
  } = useGetFirmValuationsData({
    firmId: currentFirmId,
    definitionId,
    measurementDate: resolvedMeasurementDateForEnhanced,
    fundId: isEnhancedMode ? selectedFundId : undefined,
    shouldQueryAutomatically: isValuations && enhancedDataGate,
  });

  const definitionData = isValuations ? valuationsDefinitionData : firmDefinitionData;
  const isFetchedDefinitionData = isValuations ? isFetchedValuationsDefinitionData : isFetchedFirmDefinitionData;
  const isFetchingDefinitionData = isValuations ? isFetchingValuationsDefinitionData : isFetchingFirmDefinitionData;
  const isLoadingDefinitionData = isValuations ? isLoadingValuationsDefinitionData : isLoadingFirmDefinitionData;

  const chartDataGate =
    isEnhancedMode &&
    !isNil(currentFirmId) &&
    !isNil(definitionId) &&
    isFetchedDefinitionData &&
    (isAllFundsSelected || !isNil(selectedFundId));

  // Prefetch chart data after spreadsheet data loads (non-blocking).
  // Scope to the selected fund so the popover trend matches the fund-filtered
  // table row; wait for the fund map (same gate as the definition data hook) to
  // avoid briefly firing a firm-wide chart request before the fund resolves.
  const {
    data: firmChartData,
    isLoading: isLoadingFirmChartData,
  } = useGetFirmChartData({
    firmId: currentFirmId,
    definitionId,
    fundId: isEnhancedMode ? selectedFundId : undefined,
    shouldQueryAutomatically:
      !isValuations &&
      !isNil(currentFirmId) &&
      !isNil(definitionId) &&
      isFetchedDefinitionData &&
      (isAllFundsSelected || !isNil(selectedFundId)),
  });

  const {
    data: valuationsChartData,
    isLoading: isLoadingValuationsChartData,
  } = useGetFirmValuationsChartData({
    firmId: currentFirmId,
    definitionId,
    fundId: isEnhancedMode ? selectedFundId : undefined,
    shouldQueryAutomatically: isValuations && chartDataGate,
  });

  const chartData = isValuations ? valuationsChartData : firmChartData;
  const isLoadingChartData = isValuations ? isLoadingValuationsChartData : isLoadingFirmChartData;

  const chartDataContextValue = useMemo(
    () => ({ chartData, isLoading: isLoadingChartData, selectedDate: resolvedMeasurementDateForEnhanced }),
    [chartData, isLoadingChartData, resolvedMeasurementDateForEnhanced]
  );

  const groupBy: SummaryGroupBy = useMemo(
    () => spreadsheetDefinition?.groupBy ?? 'company',
    [spreadsheetDefinition]
  );

  const sortKeys = useMemo<SortKey[]>(() => spreadsheetDefinition?.sortKeys ?? [], [spreadsheetDefinition]);
  const filters = useMemo<Filter[]>(() => spreadsheetDefinition?.filters ?? [], [spreadsheetDefinition]);

  // Backend returns rows already scoped to the selected fund (or firm-wide for All Funds)
  // and sorted per the definition, so no client-side filtering/sorting is needed.
  const orderedFundsByCompany = useMemo<FundByCompany[]>(
    () => normalizeDefinitionRows(definitionData?.rows ?? [], resolvedMeasurementDateForEnhanced),
    [definitionData, resolvedMeasurementDateForEnhanced]
  );

  const hasDraftFunds = useMemo<boolean>(
    () => orderedFundsByCompany.some(fund => !fund.is_published && !fund.is_final),
    [orderedFundsByCompany]
  );

  const hasNoCompanies = useMemo<boolean>(
    () => !isNil(currentCompanies) && isEmpty(currentCompanies),
    [currentCompanies]
  );

  const hasNoInvestments = useMemo<boolean>(
    () =>
      hasNoMeasurementDates ||
      (Boolean(isFetchedDefinitionData) &&
        (isNil(definitionData) || isEmpty(definitionData?.rows)) &&
        isEmpty(orderedFundsByCompany)),
    [definitionData, hasNoMeasurementDates, isFetchedDefinitionData, orderedFundsByCompany]
  );

  const isLoading = useMemo<boolean>(
    () =>
      isLoadingDefinitionData ||
      isLoadingMeasurementDate ||
      (isProcessManagementEnabled && isLoadingTasksProgress),
    [isLoadingDefinitionData, isLoadingMeasurementDate, isLoadingTasksProgress, isProcessManagementEnabled]
  );

  let firmSummaryExportStatus: 'loading' | 'missing' | 'ready' = 'ready';
  if (isLoading) {
    firmSummaryExportStatus = 'loading';
  } else if (hasNoCompanies || hasNoInvestments) {
    firmSummaryExportStatus = 'missing';
  }
  useExportSignal({
    target: PdfExportTargets.FIRM_SUMMARY.id,
    status: firmSummaryExportStatus,
  });

  // Spreadsheet

  const rowGroupParentKeys = useMemo<string[]>(
    () =>
      uniq(
        orderedFundsByCompany
          .map(item => getObjectValue(item).parent_key)
          .filter(Boolean)
          .map(key => toString(key))
      ),
    [orderedFundsByCompany]
  );

  const rowGroupsInitialState = useMemo<RowsGroups>(
    () =>
      Object.fromEntries(rowGroupParentKeys.map(key => [key, isAllFundsSelected ? SYSTEM_COLLAPSE : SYSTEM_EXPAND])),
    [isAllFundsSelected, rowGroupParentKeys]
  );

  const [rowGroups, setRowGroups] = useReducer(rowsGroupsReducer, rowGroupsInitialState);

  useEffect(() => {
    if (rowGroupParentKeys.length > 0) {
      setRowGroups({ type: RESET, payload: Object.fromEntries(rowGroupParentKeys.map(key => [key, isAllFundsSelected ? SYSTEM_COLLAPSE : SYSTEM_EXPAND])) });
    }
  }, [rowGroupParentKeys, isAllFundsSelected]);

  const { spreadsheet: firmSummarySpreadsheet } = useFirmSummarySheet({
    fundsAvailable,
    fundsByCompany: orderedFundsByCompany,
    groupBy,
    isAllFundsSelected,
    selectedFund,
    setSelectedFund,
    spreadsheetDefinition,
    isProcessManagementEnabled,
    measurementDateName: processManagementMeasurementDateName,
    onSortToggle,
    tasksProgress,
    onProcessManagementTaskClick: setSelectedProcessManagementTask,
  });

  const spreadsheets = useMemo<FirmSummarySpreadsheet[]>(
    () =>
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore: private 'yLength' property
      firmSummarySpreadsheet?.yLength ? [firmSummarySpreadsheet] : [],
    [firmSummarySpreadsheet]
  );

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore: private 'shouldValidate' property
  const { onChange, workbook, workbookContextValue } = useWorkbook(spreadsheets);

  const firmSummarySpreadsheetProps = useMemo<ScalarSpreadsheetProps | null>(() => {
    if (firmSummarySpreadsheet) {
      return {
        ...firmSummarySpreadsheet,
        data: getArrayValue(firmSummarySpreadsheet?.data),
        format,
        formatDispatch,
        onChange,
        sheet: firmSummarySpreadsheet,
        workbook,
        rowGroups,
        setRowGroups,
        columnWidths: activeColumnWidths,
        onColumnResize: handleColumnResize,
        columnTypes: renderedColumnTypes,
        onColumnReorder: handleColumnReorder,
        onAddColumn,
      } as unknown as ScalarSpreadsheetProps;
    }

    return null;
  }, [
    firmSummarySpreadsheet,
    format,
    formatDispatch,
    onChange,
    rowGroups,
    workbook,
    activeColumnWidths,
    handleColumnResize,
    renderedColumnTypes,
    handleColumnReorder,
    onAddColumn,
  ]);

  const { isExportMode } = useLayoutContext();

  const hasExpandedRowGroup = useMemo(
    () => Object.values(rowGroups).some(rowGroup => [SYSTEM_EXPAND, USER_EXPAND].includes(rowGroup)),
    [rowGroups]
  );

  const firmSummarySpreadsheetContent = useMemo<JSX.Element | null>(() => {
    if (hasNoCompanies) {
      return null;
    }

    // Filters reduced the result set to zero rows — let the user know via chips bar
    const isFilteredEmpty = filters.length > 0 && hasNoInvestments;

    if (hasNoInvestments && !isFilteredEmpty) {
      return null;
    }

    const aiInput = !isExportMode ? (
      <AIAssistantInput
        onSubmit={handleAIAssistSubmit}
        isLoading={aiAssist.isPending}
        disabled={!activeDefinition}
      />
    ) : null;

    const sortChipsBar = sortKeys.length > 0 && !isExportMode ? (
      <SortChipsBar
        sortKeys={sortKeys}
        valueTypeById={valueTypeById ?? new Map()}
        onClearAll={onClearSorts ?? (() => Promise.resolve())}
        onRemoveSort={onRemoveSort ?? (() => Promise.resolve())}
      />
    ) : null;

    const filterChipsBar = filters.length > 0 && !isExportMode ? (
      <FilterChipsBar
        filters={filters}
        valueTypeById={valueTypeById ?? new Map()}
        onClearAll={onClearFilters ?? (() => Promise.resolve())}
        onRemoveFilter={onRemoveFilter ?? (() => Promise.resolve())}
      />
    ) : null;

    let gridContent: JSX.Element | null | false = null;
    if (isLoading) {
      gridContent = <GridSkeleton maxColumns={12} maxRows={4} />;
    } else if (isFilteredEmpty) {
      gridContent = (
        <Box>
          {aiInput}
          {sortChipsBar}
          {filterChipsBar}
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <Typography variant="body2" color="text.secondary">
              No results match the active filters.
            </Typography>
          </Box>
        </Box>
      );
    } else if (firmSummarySpreadsheetProps && firmSummarySpreadsheet) {
      gridContent = (
        <ChartDataContext.Provider value={chartDataContextValue}>
          <WorkbookContext.Provider value={workbookContextValue}>
            <CustomCompactContainer>
              <Grid
                className={clsx(FIRM_SUMMARY_CONTAINER_CLASSNAME, { expanded: hasExpandedRowGroup })}
                size={{ xs: 12 }}>
                {aiInput}
                {sortChipsBar}
                {filterChipsBar}
                <CustomScalarSpreadsheet
                  {...firmSummarySpreadsheetProps}
                  isLoading={
                    isFetchingDefinitionData ||
                    (isProcessManagementEnabled && isFetchingTasksProgress)
                  }
                />
              </Grid>

              {!isExportMode && (
                <InformationMessage
                  displayMessage={hasDraftFunds}
                  message={FIRM_SUMMARY_VALUES_SHOWN_PUBLISHED_TITLE}
                />
              )}
            </CustomCompactContainer>
          </WorkbookContext.Provider>
        </ChartDataContext.Provider>
      );
    }

    return (
      <CustomContainer data-ref={PdfExportTargets.FIRM_SUMMARY.id}>
        <Grid className={FIRM_SUMMARY_CONTAINER_CLASSNAME} size={{ xs: 12 }}>
          {gridContent}
        </Grid>
      </CustomContainer>
    );
  }, [
    activeDefinition,
    aiAssist.isPending,
    chartDataContextValue,
    filters,
    firmSummarySpreadsheet,
    firmSummarySpreadsheetProps,
    handleAIAssistSubmit,
    hasDraftFunds,
    hasExpandedRowGroup,
    hasNoCompanies,
    hasNoInvestments,
    isFetchingDefinitionData,
    isFetchingTasksProgress,
    isExportMode,
    isLoading,
    isProcessManagementEnabled,
    onClearFilters,
    onClearSorts,
    onRemoveFilter,
    onRemoveSort,
    sortKeys,
    valueTypeById,
    workbookContextValue,
  ]);

  const firmSummaryTabContent = useMemo<JSX.Element | null>(() => {
    if (!firmSummarySpreadsheetContent) {
      return null;
    }

    return (
      <>
        {firmSummarySpreadsheetContent}
        {selectedProcessManagementTask && (
          <TaskProgressDialog
            {...selectedProcessManagementTask}
            open
            onClose={closeProcessManagementTaskDialog}
            firmName={toString(getObjectValue(currentFirm).name)}
          />
        )}
      </>
    );
  }, [closeProcessManagementTaskDialog, currentFirm, firmSummarySpreadsheetContent, selectedProcessManagementTask]);

  // Tab
  const firmSummaryTab = useMemo<Tab>(
    () =>
      ({
        id: FIRM_SUMMARY_KEY,
        label: FIRM_SUMMARY_TITLE,
        content: firmSummaryTabContent,
      }) as Tab,
    [firmSummaryTabContent]
  );

  const isFilteredEmpty = filters.length > 0 && hasNoInvestments;

  return { firmSummaryTab, hasNoCompanies, hasNoInvestments, isFilteredEmpty };
};

export default useFirmSummaryTab;
